import { TERMINAL_FAILURE_STATUSES, type Action, type DealStep } from '@/types/deal';

/**
 * Picks the step that should render as Active. The server is the
 * source of truth: `Action#active_step` (in
 * `marketpryce-rewards/app/models/action.rb`) computes which step is
 * active and serializes the result as `action.active_step_key`.
 * Mobile is a thin renderer.
 *
 * - No action yet: step 1 — the click step the user needs to tap.
 * - Action exists with `active_step_key`: look it up by key.
 * - Action exists but no `active_step_key`: server says no step is
 *   active (e.g. all done in a transient post-paid state). Render
 *   nothing as active.
 *
 * Returns undefined when steps is empty, when the server says nothing
 * is active, or when the keyed step doesn't exist in the deal's
 * current `steps_config` (server/client schema drift).
 */
export function pickActiveStep(steps: DealStep[], action?: Action): DealStep | undefined {
  if (steps.length === 0) return undefined;
  if (!action) return steps[0];
  if (!action.active_step_key) return undefined;
  return steps.find((s) => s.key === action.active_step_key);
}

/**
 * True when the user is in a terminal-failure status AND the step
 * is the one the server picked as active. Render this step alone —
 * replace the stepper — because there's nothing for the user to do
 * beyond reading the outcome.
 *
 * Driven entirely by `action.status` + `action.active_step_key` so
 * mobile stays in lockstep with the server's state machine.
 */
export function isTerminalFailureStep(step: DealStep, action?: Action): boolean {
  if (!action) return false;
  if (!TERMINAL_FAILURE_STATUSES.includes(action.status)) return false;
  return step.key === action.active_step_key;
}
